import React, { useState } from "react"
import { AvatarGroup } from "./AvatarGroup"
import CustomButton from "../OnboardingComponents/CustomButton"
import EmptyState from "../Common/EmptyState"
import TruncatedText from "../Common/TruncatedText"

const MAX_VISIBLE_CATEGORIES = 2

const formatStatusLabel = status => {
  if (status === "in-progress") return "In-Progress"
  if (status === "past") return "Past"
  if (status === "future") return "Future"
  return "—"
}

const hashSeed = (seed = "") => {
  let hash = 0
  for (let i = 0; i < seed.length; i += 1) {
    hash = seed.charCodeAt(i) + ((hash << 5) - hash)
  }
  return Math.abs(hash % 360)
}

/** Stable per-category colors (same seed → same color) */
const getCategoryColors = seed => {
  const hue = hashSeed(seed)
  return {
    background: "transparent",
    color: `hsl(${hue}, 90%, 40%)`,
    borderColor: `hsl(${hue}, 85%, 55%)`,
  }
}

const getCodeColors = seed => {
  const hue = hashSeed(seed)
  return {
    background: `hsl(${hue}, 85%, 90%)`,
    color: `hsl(${hue}, 90%, 40%)`,
  }
}

const rowCategories = row => {
  if (Array.isArray(row.categories) && row.categories.length > 0) {
    return row.categories.filter(Boolean)
  }
  if (row.category) return [row.category]
  return []
}

/**
 * ProjectsTable — Active Projects on Dashboard (Figma).
 * Category chips match Projects Listing: multi-chip + overflow count, seeded colors.
 */
const ProjectsTable = ({
  rows = [],
  arrowIconSrc,
  onViewDetail,
  isLoading = false,
}) => {
  const [tooltip, setTooltip] = useState(null)

  const handleTooltipEnter = (e, content) => {
    const rect = e.currentTarget.getBoundingClientRect()
    setTooltip({
      content,
      x: rect.left + rect.width / 2,
      y: rect.top - 8,
    })
  }

  const handleTooltipLeave = () => setTooltip(null)

  if (isLoading) {
    return (
      <div
        className="projects-table"
        role="table"
        aria-label="Active Projects"
        aria-busy="true"
      >
        <div
          className="projects-table__row projects-table__row--head"
          role="row"
        >
          <div role="columnheader">Project Title</div>
          <div role="columnheader">Category</div>
          <div role="columnheader">Status</div>
          <div role="columnheader">Collaborators</div>
          <div role="columnheader">Days Remaining</div>
          <div role="columnheader">Start Date</div>
          <div role="columnheader">End Date</div>
          <div role="columnheader" className="projects-table__actions-head">
            Actions
          </div>
        </div>
        {Array.from({ length: 5 }).map((_, i) => (
          <div
            key={`skeleton-${i}`}
            className="projects-table__row projects-table__row--skeleton"
            aria-hidden="true"
          >
            {Array.from({ length: 8 }).map((__, cellIdx) => (
              <div key={cellIdx} className="projects-table__skeleton-cell">
                <div className="projects-table__skeleton-bar" />
              </div>
            ))}
          </div>
        ))}
      </div>
    )
  }

  if (!rows || rows.length === 0) {
    return (
      <EmptyState
        size="sm"
        title="No active projects"
        description="Active projects will appear here once they are in progress."
      />
    )
  }

  return (
    <>
      <div className="projects-table" role="table" aria-label="Active Projects">
        <div
          className="projects-table__row projects-table__row--head"
          role="row"
        >
          <div role="columnheader">Project Title</div>
          <div role="columnheader">Category</div>
          <div role="columnheader">Status</div>
          <div role="columnheader">Collaborators</div>
          <div role="columnheader">Days Remaining</div>
          <div role="columnheader">Start Date</div>
          <div role="columnheader">End Date</div>
          <div role="columnheader" className="projects-table__actions-head">
            Actions
          </div>
        </div>
        {rows.map(row => {
          const categories = rowCategories(row)
          const codeColors = getCodeColors(row.title || row.code || "")
          return (
            <div key={row.id} className="projects-table__row" role="row">
              <div
                className="projects-table__cell projects-table__cell--title"
                role="cell"
              >
                <span className="projects-table__code" style={codeColors}>
                  {row.code}
                </span>
                <TruncatedText
                  as="button"
                  type="button"
                  className="projects-table__title-btn"
                  onClick={() => onViewDetail?.(row)}
                >
                  {row.title}
                </TruncatedText>
              </div>
              <div
                className="projects-table__cell projects-table__cell--categories"
                role="cell"
              >
                {categories.length > 0 ? (
                  <div className="projects-table__categories">
                    {categories
                      .slice(0, MAX_VISIBLE_CATEGORIES)
                      .map((cat, idx) => (
                        <span
                          key={`${row.id}-cat-${idx}`}
                          className="projects-table__category"
                          style={getCategoryColors(cat)}
                        >
                          {cat}
                        </span>
                      ))}
                    {categories.length > MAX_VISIBLE_CATEGORIES && (
                      <span
                        className="projects-table__category projects-table__category--more"
                        onMouseEnter={e =>
                          handleTooltipEnter(
                            e,
                            categories.slice(MAX_VISIBLE_CATEGORIES).join(", "),
                          )
                        }
                        onMouseLeave={handleTooltipLeave}
                      >
                        +{categories.length - MAX_VISIBLE_CATEGORIES}
                      </span>
                    )}
                  </div>
                ) : (
                  <span>—</span>
                )}
              </div>
              <div className="projects-table__cell" role="cell">
                <span
                  className={`projects-table__status-badge projects-table__status-badge--${
                    row.status || "unknown"
                  }`}
                >
                  {formatStatusLabel(row.status)}
                </span>
              </div>
              <div
                className="projects-table__cell projects-table__cell--avatars"
                role="cell"
              >
                <AvatarGroup people={row.collaborators} size="xs" max={4} />
              </div>
              <div className="projects-table__cell" role="cell">
                <TruncatedText>{row.daysRemaining}</TruncatedText>
              </div>
              <div className="projects-table__cell" role="cell">
                <TruncatedText>{row.startDate}</TruncatedText>
              </div>
              <div className="projects-table__cell" role="cell">
                <TruncatedText>{row.endDate}</TruncatedText>
              </div>
              <div
                className="projects-table__cell projects-table__cell--actions"
                role="cell"
              >
                <CustomButton
                  variant="secondary"
                  size="sm"
                  type="button"
                  trailingIconSrc={arrowIconSrc}
                  trailingIconAlt=""
                  onClick={() => onViewDetail?.(row)}
                >
                  View Detail
                </CustomButton>
              </div>
            </div>
          )
        })}
      </div>

      {tooltip && (
        <div
          className="projects-table__tooltip"
          style={{
            position: "fixed",
            left: `${tooltip.x}px`,
            top: `${tooltip.y}px`,
            transform: "translate(-50%, -100%)",
            zIndex: 1000,
          }}
        >
          {tooltip.content}
        </div>
      )}
    </>
  )
}

export default ProjectsTable
